import { useEffect, useMemo, useState } from "react";
import { usePrava } from "@/api/hooks/useAuth";
import {
  ClipboardList,
  Download,
  FileDown,
  FileSpreadsheet,
  FileText,
  Layers,
  PackageCheck,
  Receipt,
  Scissors,
  Send,
  Table2,
  Truck,
} from "lucide-react";
import { jeLaminatRiadok } from "@/lib/operacie";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  fetchDodaciListPdf,
  fetchKalkulaciaPdf,
  fetchNarezovyPlanPdf,
  fetchNakladyMaterialuPdf,
  fetchObjednavkaPdf,
  fetchPlanLaminatovPdf,
  fetchPorezPlanPdf,
  fetchZakazkaPdf,
  stiahniPonuku,
  type FormatPonuky,
  type PdfVystup,
  type Varianty,
} from "@/lib/pdfDownload";
import {
  stiahniDielceExport,
  type ExportFormat,
} from "@/lib/exportDownload";
import { PdfNahladDialog } from "@/components/zakazka/PdfNahladDialog";
import {
  PonukaExportDialog,
  type DokumentSoZostavami,
  type VyberDokumentu,
} from "@/components/zakazka/PonukaExportDialog";
import { VyberVariantDialog } from "@/components/zakazka/VyberVariantDialog";
import { jeTovarovaVarianta, poradieVariant } from "@/lib/variantyZakazky";
import { CutriteDialog } from "@/components/zakazka/CutriteDialog";
import { OTVOR_CUTRITE, type OtvorCutriteDetail } from "@/components/zakazka/PasCutrite";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import type { Varianta, Zostava, ZakazkaDetail } from "@/api/schemas";

/** Výstup, ktorý pri viac variantách najprv čaká na výber (KOLO 206). */
interface VystupVariant {
  titul: string;
  potvrdit: string;
  poznamka?: string;
  spusti: (varianty: Varianty) => void;
}

/**
 * Jedno miesto pre všetky PDF výstupy zákazky.
 *
 * Predtým boli rozsypané na dvoch miestach — štyri tlačidlá v karte „Cena
 * celkom" (kam vecne nepatria) a jedno v paneli porezu. V bočnom paneli
 * zaberali skoro 200 px, ktoré teraz patria kusovníku.
 *
 * Klik neuloží súbor rovno — otvorí náhľad (`PdfNahladDialog`), kde je
 * vidno, čo sa uloží, a dá sa to aj vytlačiť. **Výnimkou je tabuľkový
 * export** (KOLO 71): náhľad renderuje PDF v iframe a tabuľku by v ňom
 * prehliadač nezobrazil, takže XLSX aj CSV sa sťahujú priamo.
 *
 * Pri zákazke s viac variantami (časti, výpočty, výrobné — KOLO 206) sa
 * najprv vyberá, z ktorých — predvolená je otvorená záložka. Ponuka
 * a dodací list majú výber v okne zostáv (strom varianta → zostavy).
 */
export function VystupyMenu({
  zakazka,
  varianta,
}: {
  zakazka: ZakazkaDetail;
  varianta: Varianta;
}): JSX.Element {
  const [bezi, setBezi] = useState<string | null>(null);
  // Okno výberu zostáv — pre ponuku aj dodací list (KOLO 185). Dokument
  // ostáva aj po zatvorení, nech nadpis pri zatváraní nepreskočí.
  const [vyber, setVyber] = useState<{
    dokument: DokumentSoZostavami;
    open: boolean;
  }>({ dokument: "ponuka", open: false });
  const [cutriteOpen, setCutriteOpen] = useState(false);
  // Pás zmeny otvorí okno na svojom zozname (KOLO 207)
  const [cutriteVyber, setCutriteVyber] = useState<string[] | null>(null);
  // Výber variant pred výstupom — výstup ostáva aj po zatvorení (nadpis)
  const [vyberVariant, setVyberVariant] = useState<{
    vystup: VystupVariant | null;
    open: boolean;
  }>({ vystup: null, open: false });
  // Pás „zákazka sa zmenila" v editore otvorí to isté okno (KOLO 153)
  useEffect(() => {
    const otvor = (e: Event): void => {
      const detail = (e as CustomEvent<OtvorCutriteDetail | null>).detail;
      setCutriteVyber(detail?.varianta_ids ?? null);
      setCutriteOpen(true);
    };
    window.addEventListener(OTVOR_CUTRITE, otvor);
    return () => window.removeEventListener(OTVOR_CUTRITE, otvor);
  }, []);
  const [nahlad, setNahlad] = useState<{
    nazov: string;
    vystup: PdfVystup | null;
  } | null>(null);

  const aktivneZostavy = useMemo(
    () =>
      ((varianta.zostavy ?? []) as Zostava[]).filter(
        (z) => !("deleted_at" in z) || !z.deleted_at,
      ),
    [varianta],
  );
  // Varianty v poradí záložiek; otvorená z propu (čerstvé dáta editora)
  const varianty = useMemo(() => {
    const zoznam = poradieVariant((zakazka.varianty ?? []) as Varianta[]);
    return zoznam.some((v) => v.id === varianta.id)
      ? zoznam.map((v) => (v.id === varianta.id ? varianta : v))
      : [varianta];
  }, [zakazka.varianty, varianta]);
  const viacVariant = varianty.length > 1;
  // Lamináty pod doskami v niektorej variante (KOLO 234) — inak je Plán
  // rezania laminátov sivý s dôvodom
  const maLaminaty = useMemo(
    () =>
      varianty.some((v) =>
        (v.zostavy ?? []).some((z) => (z.dielce ?? []).some((d) => jeLaminatRiadok(d))),
      ),
    [varianty],
  );
  // Jedna ponuka z nábytku aj tovaru (KOLO 230) — aktívny výpočet / výroba
  // a aktívny tovar sú v okne ponuky a dodacieho listu zaškrtnuté hneď
  // Prázdny odbor do ponuky nejde (KOLO 231, chyba z KOLA 230): zákazka
  // len s regálmi mala oddiel „Nábytok 0 €" z nábytku A, ktorý vznikol so
  // zákazkou — to isté pravidlo ako `vyber_variant.predvolene_pre_ponuku`
  const predvoleneDoPonuky = useMemo(() => {
    const tovar = varianty.find((v) => v.id === zakazka.aktivna_tovar_varianta_id);
    const nabytok = varianty.find((v) => v.id === zakazka.aktivna_varianta_id);
    const maZostavy = (v: Varianta | undefined): boolean => (v?.zostavy ?? []).length > 0;
    if (!tovar || !nabytok || !maZostavy(tovar)) return undefined;
    return maZostavy(nabytok) ? [nabytok.id, tovar.id] : [tovar.id];
  }, [zakazka.aktivna_tovar_varianta_id, zakazka.aktivna_varianta_id, varianty]);
  // Cut Rite posiela rozpočtár (KOLO 231)
  const prava = usePrava();

  /**
   * Spoločný obal — drží „Generuje sa…" stav a hlási chyby jednotne.
   * Dialóg sa otvorí hneď (s placeholderom), aby bolo vidno, že sa niečo
   * deje aj pri PDF, ktoré sa generuje dlhšie (porezový plán s rozkresmi).
   */
  async function otvorNahlad(
    kluc: string,
    nazovVystupu: string,
    fn: () => Promise<PdfVystup>,
  ): Promise<void> {
    setBezi(kluc);
    setNahlad({ nazov: nazovVystupu, vystup: null });
    try {
      const vystup = await fn();
      setNahlad({ nazov: nazovVystupu, vystup });
      setVyber((v) => ({ ...v, open: false }));
      setVyberVariant((v) => ({ ...v, open: false }));
    } catch (err) {
      setNahlad(null);
      toast({
        variant: "destructive",
        title: `Chyba pri generovaní — ${nazovVystupu}`,
        description: extractErrorMessage(err),
      });
    } finally {
      setBezi(null);
    }
  }

  /** Pri viac variantách najprv okno výberu, inak rovno otvorená záložka. */
  function sVariantami(vystup: VystupVariant): void {
    if (viacVariant) setVyberVariant({ vystup, open: true });
    else vystup.spusti(varianta.id);
  }

  /** Tabuľkový export — bez náhľadu, rovno na disk. */
  async function tabulka(
    format: ExportFormat,
    vybrane: Varianty,
  ): Promise<void> {
    setBezi(`export-${format}`);
    try {
      await stiahniDielceExport(
        zakazka.id,
        zakazka.cislo_zakazky,
        format,
        vybrane,
      );
      setVyberVariant((v) => ({ ...v, open: false }));
    } catch (err) {
      toast({
        variant: "destructive",
        title: `Chyba pri exporte dielcov (${format.toUpperCase()})`,
        description: extractErrorMessage(err),
      });
    } finally {
      setBezi(null);
    }
  }

  function ponuka({ varianty: ids, zostavy }: VyberDokumentu): void {
    void otvorNahlad("ponuka", "Cenová ponuka", () =>
      fetchZakazkaPdf(
        zakazka.id,
        zakazka.cislo_zakazky,
        ids,
        zostavy ?? undefined,
      ),
    );
  }

  /** Ponuka v Exceli / ako e-mail pre Outlook (KOLO 226) — bez náhľadu. */
  async function ponukaInak(
    format: FormatPonuky,
    { varianty: ids, zostavy }: VyberDokumentu,
    sExcelom = false,
  ): Promise<void> {
    setBezi(`ponuka-${format}`);
    try {
      await stiahniPonuku(
        zakazka.id,
        zakazka.cislo_zakazky,
        format,
        ids,
        zostavy ?? undefined,
        sExcelom,
      );
      setVyber((v) => ({ ...v, open: false }));
      if (format === "eml") {
        toast({
          title: "E-mail s ponukou je stiahnutý",
          description:
            "Otvor ho (Stiahnuté) — v Outlooku sa otvorí ako nový e-mail. Doplň adresu a odošli.",
        });
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title:
          format === "eml"
            ? "Chyba pri príprave e-mailu s ponukou"
            : "Chyba pri exporte ponuky do Excelu",
        description: extractErrorMessage(err),
      });
    } finally {
      setBezi(null);
    }
  }

  function dodaciList({ varianty: ids, zostavy }: VyberDokumentu): void {
    void otvorNahlad("dodaci-list", "Dodací list", () =>
      fetchDodaciListPdf(
        zakazka.id,
        zakazka.cislo_zakazky,
        ids,
        zostavy ?? undefined,
      ),
    );
  }

  /**
   * Pri viacerých zostavách alebo variantách sa najprv vyberá, čo ide do
   * dokumentu. Cenová ponuka otvorí okno vždy (KOLO 229) — v ňom sú
   * tlačidlá PDF, Excel a e-mail; user: *„stačí, že je len jeden riadok
   * CP a potom, keď to otvoríš, tak tlačidlá tam"*.
   */
  function sVyberom(dokument: DokumentSoZostavami): void {
    if (dokument === "ponuka" || aktivneZostavy.length > 1 || viacVariant)
      setVyber({ dokument, open: true });
    else dodaciList({ varianty: [varianta.id], zostavy: null });
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            disabled={bezi !== null}
            title="PDF ponuka, dodací list, kalkulačný zoznam, súpis materiálu, plány rezania, tabuľka dielcov"
          >
            <FileText className="h-4 w-4" />
            {bezi ? "Generuje sa…" : "Výstupy"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Pre zákazníka</DropdownMenuLabel>
          <DropdownMenuItem
            title="PDF, Excel alebo e-mail pre Outlook — vyberieš v okne"
            onSelect={() => sVyberom("ponuka")}
          >
            <Download className="h-4 w-4" />
            Cenová ponuka
          </DropdownMenuItem>
          <DropdownMenuItem
            title="Zostavy na podpis pri odovzdaní — Odovzdal / Prevzal, bez cien"
            onSelect={() => sVyberom("dodaci-list")}
          >
            <PackageCheck className="h-4 w-4" />
            Dodací list
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Interné</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() =>
              sVariantami({
                titul: "Kalkulačný zoznam",
                potvrdit: "Zobraziť PDF",
                spusti: (vybrane) =>
                  void otvorNahlad("kalkulacia", "Kalkulačný zoznam", () =>
                    fetchKalkulaciaPdf(zakazka.id, zakazka.cislo_zakazky, vybrane),
                  ),
              })
            }
          >
            <ClipboardList className="h-4 w-4" />
            Kalkulačný zoznam
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              sVariantami({
                titul: "Súpis pre objednávky",
                potvrdit: "Zobraziť PDF",
                poznamka: "Tabule a tyče sa rátajú z dielcov všetkých vybraných spolu.",
                spusti: (vybrane) =>
                  void otvorNahlad("objednavka", "Súpis pre objednávky", () =>
                    fetchObjednavkaPdf(zakazka.id, zakazka.cislo_zakazky, vybrane),
                  ),
              })
            }
          >
            <Truck className="h-4 w-4" />
            Súpis pre objednávky
          </DropdownMenuItem>
          <DropdownMenuItem
            title="Množstvá ako súpis pre objednávky, nákupné ceny bez réžie a práce"
            onSelect={() =>
              sVariantami({
                titul: "Súpis nákladov materiálu",
                potvrdit: "Zobraziť PDF",
                poznamka: "Tabule a tyče sa rátajú z dielcov všetkých vybraných spolu.",
                spusti: (vybrane) =>
                  void otvorNahlad("naklady", "Súpis nákladov materiálu", () =>
                    fetchNakladyMaterialuPdf(zakazka.id, zakazka.cislo_zakazky, vybrane),
                  ),
              })
            }
          >
            <Receipt className="h-4 w-4" />
            Súpis nákladov materiálu
          </DropdownMenuItem>

          {/* Tovar (KOLO 230) sa nereže — výstupy pre dielňu a pílu nemá */}
          {jeTovarovaVarianta(varianta) ? null : (
          <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Pre dielňu</DropdownMenuLabel>
          {/* Server porez pri tlači počíta sám zo všetkých vybraných variant
              (`build_porez_plan`) — do KOLA 218 bola položka sivá, kým nebol
              uložený porez otvorenej záložky, a výber častí sa nedal otvoriť */}
          <DropdownMenuItem
            title="Rozkresy tabúľ pre dielňu"
            onSelect={() =>
              sVariantami({
                titul: "Porezový plán",
                potvrdit: "Zobraziť PDF",
                poznamka: "Dielce všetkých vybraných sa režú z tých istých tabúľ.",
                spusti: (vybrane) =>
                  void otvorNahlad("porez", "Porezový plán", () =>
                    fetchPorezPlanPdf(zakazka.id, zakazka.cislo_zakazky, vybrane),
                  ),
              })
            }
          >
            <FileDown className="h-4 w-4" />
            Porezový plán (tabule)
          </DropdownMenuItem>
          <DropdownMenuItem
            title="Plán krátenia dĺžkových materiálov (pracovné dosky, zásteny…)"
            onSelect={() =>
              // Bez prerezu — endpoint si ho vezme z Nastavení, aby PDF,
              // panel Rezanie a report dodávateľov počítali to isté.
              sVariantami({
                titul: "Nárezový plán",
                potvrdit: "Zobraziť PDF",
                poznamka: "Dielce všetkých vybraných sa režú z tých istých tyčí.",
                spusti: (vybrane) =>
                  void otvorNahlad("narez", "Nárezový plán", () =>
                    fetchNarezovyPlanPdf(zakazka.id, zakazka.cislo_zakazky, vybrane),
                  ),
              })
            }
          >
            <Scissors className="h-4 w-4" />
            Nárezový plán (dĺžkové)
          </DropdownMenuItem>
          {/* Lamináty pod doskami (KOLO 234) — na pílu nejdú, robotníci ich
              režú podľa vlastného plánu; bez laminátu sivá s dôvodom */}
          <DropdownMenuItem
            disabled={!maLaminaty}
            title={
              maLaminaty
                ? "Rozkres listov laminátu s prírezmi +20 / +20 pre robotníkov"
                : "Zákazka nemá lamináty — laminuj dosku v menu dielca (pravé tlačidlo → Laminovať…)"
            }
            onSelect={() =>
              sVariantami({
                titul: "Plán rezania laminátov",
                potvrdit: "Zobraziť PDF",
                poznamka: "Lamináty všetkých vybraných sa režú z tých istých listov.",
                spusti: (vybrane) =>
                  void otvorNahlad("laminaty", "Plán rezania laminátov", () =>
                    fetchPlanLaminatovPdf(zakazka.id, zakazka.cislo_zakazky, vybrane),
                  ),
              })
            }
          >
            <Layers className="h-4 w-4" />
            Plán rezania laminátov
          </DropdownMenuItem>
          {prava.nastavenia_vyroby ? (
            <DropdownMenuItem
              title="Kontrola kódov tabúľ a odoslanie na import a optimalizáciu v Cut Rite"
              onSelect={() => setCutriteOpen(true)}
            >
              <Send className="h-4 w-4" />
              Poslať do Cut Rite
            </DropdownMenuItem>
          ) : null}

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Tabuľka dielcov</DropdownMenuLabel>
          {/* Jeden plošný dielec na riadok — rozmery prírezu, materiál,
              hrany, opracovanie. Sťahuje sa priamo, nie cez náhľad. */}
          <DropdownMenuItem
            title="Excel — jeden plošný dielec na riadok (rozmery prírezu, materiál, hrany)"
            onSelect={() =>
              sVariantami({
                titul: "Podklad pre pílu — Excel",
                potvrdit: "Stiahnuť XLSX",
                spusti: (vybrane) => void tabulka("xlsx", vybrane),
              })
            }
          >
            <FileSpreadsheet className="h-4 w-4" />
            Podklad pre pílu — Excel (XLSX)
          </DropdownMenuItem>
          <DropdownMenuItem
            title="To isté ako XLSX, ale ako CSV — presne tento súbor sa posiela do Cut Rite"
            onSelect={() =>
              sVariantami({
                titul: "Podklad pre pílu — CSV",
                potvrdit: "Stiahnuť CSV",
                spusti: (vybrane) => void tabulka("csv", vybrane),
              })
            }
          >
            <Table2 className="h-4 w-4" />
            Podklad pre pílu — CSV (ten istý ide do Cut Rite)
          </DropdownMenuItem>
          </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <PonukaExportDialog
        open={vyber.open}
        onOpenChange={(open) => setVyber((v) => ({ ...v, open }))}
        dokument={vyber.dokument}
        varianty={varianty}
        otvorenaId={varianta.id}
        predvolene={predvoleneDoPonuky}
        onExport={(v) =>
          vyber.dokument === "dodaci-list" ? dodaciList(v) : ponuka(v)
        }
        onExcel={
          vyber.dokument === "ponuka" ? (v) => void ponukaInak("xlsx", v) : undefined
        }
        onMail={
          vyber.dokument === "ponuka"
            ? (v, sExcelom) => void ponukaInak("eml", v, sExcelom)
            : undefined
        }
        exporting={
          bezi === "ponuka" ||
          bezi === "dodaci-list" ||
          bezi === "ponuka-xlsx" ||
          bezi === "ponuka-eml"
        }
      />

      <VyberVariantDialog
        open={vyberVariant.open}
        onOpenChange={(open) => setVyberVariant((v) => ({ ...v, open }))}
        titul={vyberVariant.vystup?.titul ?? ""}
        potvrdit={vyberVariant.vystup?.potvrdit ?? ""}
        poznamka={vyberVariant.vystup?.poznamka}
        varianty={varianty}
        otvorenaId={varianta.id}
        onPotvrd={(ids) => vyberVariant.vystup?.spusti(ids)}
        bezi={bezi !== null}
      />

      <CutriteDialog
        open={cutriteOpen}
        onOpenChange={(o) => {
          setCutriteOpen(o);
          if (!o) setCutriteVyber(null);
        }}
        zakazkaId={zakazka.id}
        varianty={varianty}
        otvorenaId={varianta.id}
        vyber={cutriteVyber}
      />

      {nahlad ? (
        <PdfNahladDialog
          nazov={nahlad.nazov}
          vystup={nahlad.vystup}
          open
          onOpenChange={(o) => {
            if (!o) setNahlad(null);
          }}
        />
      ) : null}
    </>
  );
}
